import { format } from 'date-fns';
import { useGetMatchListQuery } from 'hooks/queries/match';
import React from 'react';
import CoreIconButton from 'shared/button/CoreIconButton';
import Icon from 'shared/icons';
import useCommonStore from 'store/useCommonStore';
import { PlatformCode } from 'type/common';
import { IMatchDB } from 'utils/dummy';
import {
	gender_mapper,
	setAgeSection,
	setFollowerGrowthRate,
} from 'utils/match/matchFilterData';
import { floatFixed, roundFloat } from 'utils/round';
import * as XLSX from 'xlsx';

// type
interface IMatchExcelCommon {
	'NO.': number;
	아이디: string;
	카테고리: string;
}

interface IMatchExcelIg extends IMatchExcelCommon {
	'아이디(full-name)': string;
	오디언스: string;
	팔로워: number;
	'유효 팔로워': number;
	'평균 도달 수': number;
	'릴스 비율': string;
	//'평균 팔로워 수 성장률 (30일)': string;
}

interface IMatchExcelYt extends IMatchExcelCommon {
	'아이디(handle)': string;
	오디언스: string;
	구독자: number;
	ER: string;
	'평균 조회 수': number;
	'쇼츠 비율': string;
	//'평균 구독자 수 성장률 (30일)': string;
}

interface IMatchExcelNb extends IMatchExcelCommon {
	'아이디(네이버)': string;
	'게시글 수': number;
	'누적 방문자': number;
	'일 방문자': number;
	'평균 조회 수': number;
	//'평균 누적 방문자 수 성장률 (30일)': string;
}

interface IMatchExcelTt extends IMatchExcelCommon {
	'아이디(handle)': string;
	오디언스: string;
	팔로워: number;
	ER: string;
	'평균 조회 수': number;
	//'평균 팔로워 수 성장률 (30일)': string;
}

// 플랫폼 별 엑셀 컬럼 넓이
const ig_excel_column: number[] = [40, 130, 160, 140, 100, 80, 80, 80, 80];
const yt_excel_column: number[] = [40, 130, 160, 140, 100, 80, 80, 80, 80];
const nb_excel_column: number[] = [40, 130, 110, 140, 80, 80, 80, 80];
const tt_excel_column: number[] = [40, 130, 110, 140, 100, 80, 80, 80];
const excel_column_mapper: Record<PlatformCode, number[]> = {
	ig: ig_excel_column,
	yt: yt_excel_column,
	nb: nb_excel_column,
	tt: tt_excel_column,
};
/** xlsx에서 사용할 형태로 변환 */
const setXlsxColumn = (target_column: number[]) =>
	target_column.map((item) => ({ wpx: item }));

/** 엑셀다운로드 버튼 컴포넌트 */
const MatchExcelDownload = () => {
	// store
	const { platform, page } = useCommonStore();
	// react-query
	const { data: match_data, isLoading: match_loading } = useGetMatchListQuery();
	/** 엑셀 파일 다운로드 */
	const downloadExcelFile = () => {
		if (match_data === null || match_loading || platform.code === null) return;

		const match_excel_data: Partial<
			IMatchExcelIg | IMatchExcelYt | IMatchExcelNb | IMatchExcelTt
		>[] = [];

		if (platform.code === 'ig')
			match_data?.results.forEach((item: IMatchDB, idx: number) => {
				const match_excel_item = {
					'NO.': idx + 1,
					아이디: item.username,
					'아이디(full-name)': item.full_name?.normalize('NFC'),
					카테고리: [item.category_1, item.category_2]
						.join(', ')
						.replace(/(^,\s|,\s$)/g, ''),
					오디언스:
						item.audience_age === null || item.audience_gender === null
							? '리포트 분석 전 담당자 문의'
							: `${setAgeSection(item.audience_age)} ${
									gender_mapper[item.audience_gender]
							  }`,
					팔로워: item.follower_count,
					'유효 팔로워': item.real_follower_count,
					'평균 도달 수': item.reach_score,
					'릴스 비율':
						item.sub_video_rate === null
							? ''
							: floatFixed(roundFloat(item.sub_video_rate * 100, 'down', 2), 2) + '%',
					//'평균 팔로워 수 성장률 (30일)': setFollowerGrowthRate(item),
				};

				match_excel_data.push(match_excel_item);
			});
		else if (platform.code === 'yt')
			match_data?.results.forEach((item: IMatchDB, idx: number) => {
				const match_excel_item = {
					'NO.': idx + 1,
					아이디: item.username,
					'아이디(channel-id 또는 handle)': item.full_name,
					카테고리: [item.category_1, item.category_2]
						.join(', ')
						.replace(/(^,\s|,\s$)/g, ''),
					오디언스:
						item.audience_age === null || item.audience_gender === null
							? '리포트 분석 전 담당자 문의'
							: `${setAgeSection(item.audience_age)} ${
									gender_mapper[item.audience_gender]
							  }`,
					구독자: item.follower_count,
					ER:
						item.real_engagement === null
							? '-'
							: floatFixed(roundFloat(item.real_engagement, 'down', 2), 2) + '%',
					'평균 조회 수': item.reach_score,
					'쇼츠 비율':
						item.sub_video_rate === null
							? ''
							: floatFixed(roundFloat(item.sub_video_rate * 100, 'down', 2), 2) + '%',
					//'평균 구독자 수 성장률 (30일)': setFollowerGrowthRate(item),
				};

				match_excel_data.push(match_excel_item);
			});
		else if (platform.code === 'nb')
			match_data?.results.forEach((item: IMatchDB, idx: number) => {
				const match_excel_item = {
					'NO.': idx + 1,
					아이디: item.username,
					'아이디(네이버)': item.full_name,
					카테고리: [item.category_1, item.category_2]
						.join(', ')
						.replace(/(^,\s|,\s$)/g, ''),
					'게시글 수': item.post_count,
					'누적 방문자': item.total_visitor,
					'일 방문자': item.avg_today_visitor,
					'평균 조회 수': item.reach_score,
					//'평균 누적 방문자 수 성장률 (30일)': setFollowerGrowthRate(item),
				};

				match_excel_data.push(match_excel_item);
			});
		else if (platform.code === 'tt')
			match_data?.results.forEach((item: IMatchDB, idx: number) => {
				const match_excel_item = {
					'NO.': idx + 1,
					아이디: item.username,
					'아이디(handle)': item.full_name,
					카테고리: [item.category_1, item.category_2]
						.join(', ')
						.replace(/(^,\s|,\s$)/g, ''),
					오디언스:
						item.audience_age === null || item.audience_gender === null
							? '리포트 분석 전 담당자 문의'
							: `${setAgeSection(item.audience_age)} ${
									gender_mapper[item.audience_gender]
							  }`,
					팔로워: item.follower_count,
					ER:
						item.real_engagement === null
							? '-'
							: floatFixed(roundFloat(item.real_engagement, 'down', 2), 2) + '%',
					'평균 조회 수': item.reach_score,
					//'평균 팔로워 수 성장률 (30일)': setFollowerGrowthRate(item),
				};

				match_excel_data.push(match_excel_item);
			});

		const new_sheet = XLSX.utils.book_new();
		const match_sheet_data = XLSX.utils.json_to_sheet(match_excel_data, {
			skipHeader: false,
		});

		match_sheet_data['!cols'] = setXlsxColumn(excel_column_mapper[platform.code]);

		/** 날짜 포맷 */
		const formatter = (date: Date) => format(date, 'yyMMdd_HHmmss');

		XLSX.utils.book_append_sheet(new_sheet, match_sheet_data, 'IMMS - Match data');
		XLSX.writeFile(
			new_sheet,
			`match_${platform.code}_p${page}_${formatter(new Date())}.xlsx`,
		);
	};

	return (
		<CoreIconButton
			color_type="gray"
			style_type="outlined"
			text="엑셀 다운로드"
			_onClick={downloadExcelFile}
			_disabled={match_data === null || match_loading}
		>
			<Icon.Download />
		</CoreIconButton>
	);
};

export default MatchExcelDownload;
